import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  BrainIcon,
  CodeIcon,
  FilesIcon,
  FlaskConicalIcon,
  GaugeIcon,
  ListTodoIcon,
  ShapesIcon,
  PresentationIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UsersIcon,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  type JSX,
} from 'react';

import { CtLogo } from '@/components/brand/CtLogo.js';
import { useDeveloperFlag } from '@/lib/api/feature-flags.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { ScrollArea } from '@/components/ui/scroll-area.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { SidebarAutomations } from '@/features/automations/SidebarAutomations.js';
import {
  isOverviewReport,
  isReportPath,
} from '@/features/code-quality/report-origin.js';
import { SidebarDashboards } from '@/features/dashboards/SidebarDashboards.js';
import { useHintTarget } from '@/features/onboarding/useHintTarget.js';
import { SidebarConversations } from '@/features/projects/SidebarConversations.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { onMemoryProposalsChanged } from '@/features/memory/memory-proposal-feed.js';
import { memoryNavVisible } from '@/features/memory/memory-nav.js';
import { skillsNavVisible } from '@/features/workspace/skills-nav.js';
import { ConnectorsDirectoryModal } from '@/integration/components/connectors/ConnectorsDirectoryModal.js';
import {
  memoryKeys,
  pendingMemoryProposalsQueryOptions,
} from '@/lib/api/memory.js';
import type { Skill } from '@/lib/api/skills.js';
import { WorkspaceSwitcher } from '@/features/workspace/WorkspaceSwitcher.js';
import { workspaceQueryOptions } from '@/lib/api/workspaces.js';
import { getAuthHeaders } from '@/identity.js';
import { useServerEvent } from '@/integration/runtime/server-events.js';
import { cn } from '@/lib/utils.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

import {
  sidebarIconClass,
  sidebarRowActive,
  sidebarRowBase,
  sidebarRowHover,
} from './sidebar-primitives.js';
import { InstallAppCard } from '@/features/pwa-install/InstallAppCard.js';

interface PreferencesView {
  showAdminLink: boolean;
}

/**
 * Fetches the signed-in user's preferences with the platform JWT only
 * (zero-trust — the server derives identity from the verified Bearer). Uses
 * a dedicated query rather than the Teams SSO `/v1/me` path, which can fail
 * closed when `getAuthToken()` is unavailable — the sidebar should still
 * react to a toggle change in those cases.
 */
async function fetchPrefs(): Promise<PreferencesView> {
  const headers = await getAuthHeaders();
  const r = await fetch('/v1/me/preferences', {
    method: 'GET',
    headers,
    credentials: 'same-origin',
  });
  if (!r.ok) throw new Error(`prefs fetch failed: ${r.status}`);
  return (await r.json()) as PreferencesView;
}

function ComposeIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
    >
      <path d="M2.669 11.333V8.667c0-.922 0-1.655.048-2.244.048-.597.15-1.106.387-1.571l.155-.276a4 4 0 0 1 1.593-1.472l.177-.083c.418-.179.872-.263 1.395-.305.589-.048 1.32-.048 2.243-.048h.5a.665.665 0 0 1 0 1.33h-.5c-.944 0-1.613 0-2.135.043-.386.032-.66.085-.876.162l-.2.086a2.67 2.67 0 0 0-1.064.982l-.102.184c-.126.247-.206.562-.248 1.076-.043.523-.043 1.192-.043 2.136v2.666c0 .944 0 1.613.043 2.136.042.514.122.829.248 1.076l.102.184c.257.418.624.758 1.064.982l.2.086c.217.077.49.13.876.161.522.043 1.19.044 2.135.044h2.667c.944 0 1.612-.001 2.135-.044.514-.042.829-.121 1.076-.247l.184-.104c.418-.256.759-.623.983-1.062l.086-.2c.077-.217.13-.49.16-.876.043-.523.044-1.192.044-2.136v-.5a.665.665 0 0 1 1.33 0v.5c0 .922.001 1.655-.047 2.244-.043.522-.127.977-.306 1.395l-.083.176a4 4 0 0 1-1.471 1.593l-.276.154c-.466.238-.975.34-1.572.39-.59.047-1.321.047-2.243.047H8.667c-.923 0-1.654 0-2.243-.048-.523-.043-.977-.126-1.395-.305l-.177-.084a4 4 0 0 1-1.593-1.471l-.155-.276c-.237-.465-.339-.974-.387-1.57-.049-.59-.048-1.322-.048-2.245m10.796-8.22a2.43 2.43 0 0 1 3.255.167l.167.185c.727.892.727 2.18 0 3.071l-.168.185-5.046 5.048a4 4 0 0 1-1.945 1.072l-.317.058-1.817.26a.665.665 0 0 1-.752-.753l.26-1.816.058-.319a4 4 0 0 1 1.072-1.944L13.28 3.28zm2.314 1.108a1.103 1.103 0 0 0-1.476-.076l-.084.076-5.046 5.048a2.67 2.67 0 0 0-.716 1.296l-.04.212-.134.939.94-.134.211-.039a2.67 2.67 0 0 0 1.298-.716L15.78 5.78l.076-.084c.33-.404.33-.988 0-1.392z" />
    </svg>
  );
}

interface SidebarProps {
  collapsed?: boolean;
  hideCollapseButton?: boolean;
  onNavigate?: () => void;
}

export function Sidebar({
  collapsed = false,
  onNavigate,
}: SidebarProps): JSX.Element {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const isAdmin = (identity.identity?.roles ?? []).includes('admin');
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);

  // Pull `prefs.showAdminLink` via a dedicated query rather than relying on
  // /v1/me, which can return 401 when Teams `getAuthToken()` is unavailable.
  // The fetch authenticates with the platform JWT only; the query is keyed on
  // the cached identity's local users.id so it re-fetches when the user changes.
  // Authoritative live updates come from the SSE `prefs.changed` event below;
  // refetchOnWindowFocus + the 60s interval are the safety net for clients
  // that lost the SSE channel.
  const qc = useQueryClient();
  const localUserId = identity.identity?.userId ?? null;
  const prefsQuery = useQuery({
    queryKey: ['me-preferences', localUserId],
    queryFn: fetchPrefs,
    enabled: isAdmin && !!localUserId,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  useServerEvent(
    'prefs.changed',
    useCallback(() => {
      void qc.invalidateQueries({ queryKey: ['me-preferences'] });
    }, [qc]),
  );
  const showAdminPanelLink = isAdmin && prefsQuery.data?.showAdminLink === true;

  // Left-menu section visibility from the unified runtime config (GET /v1/config).
  // Fail-open: the loader defaults each flag to true, so a config blip never hides a section.
  const features = getRuntimeConfig().features;
  const showDashboards = features.dashboards;
  const showDiagrams = features.diagrams;
  const showQuality = features.quality;
  const showAutomations = features.automations;
  const showDecks = features.decks;

  // Global admin toggle: hide the Developer section when disabled. Fail-open.
  const showDeveloper = useDeveloperFlag();

  /**
   * Quality and Developer are opt-in per workspace on top of their global flags: each nav entry shows
   * only when the selected workspace has the matching domain (`quality` / `developer`) enabled. The
   * detail query runs once either global flag is on and a concrete workspace is selected; while it
   * loads — or when the domain is absent/disabled — the entry stays hidden. Hiding until known is the
   * least-flashy choice (no show-then-hide flicker on load).
   */
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);
  const workspaceQuery = useQuery({
    ...workspaceQueryOptions(selectedWorkspaceId ?? ''),
    enabled:
      (showQuality ||
        showDeveloper ||
        features.skills ||
        features.memory ||
        features.codeQuality ||
        features.taskTracker) &&
      selectedWorkspaceId !== null,
  });
  const showQualityEntry =
    showQuality &&
    workspaceQuery.data?.domains?.find((domain) => domain.domain === 'quality')
      ?.enabled === true;
  const developerDomainOn =
    workspaceQuery.data?.domains?.find(
      (domain) => domain.domain === 'developer',
    )?.enabled === true;
  const showDeveloperEntry = showDeveloper && developerDomainOn;
  /**
   * Code Quality and Contributors read the projects of the workspace's Digital Developer and sit under
   * its row, so they show only with that row. Their own flag fails closed, unlike the Developer
   * switch: only an explicit `true` shows them.
   */
  const showCodeQualityEntries =
    features.codeQuality === true && showDeveloperEntry;
  /**
   * The task tracker is one per person and reads the person's own connections, so its row shows only
   * in the personal workspace, and only for an explicit `true` of its flag.
   */
  const showTasks =
    features.taskTracker === true && workspaceQuery.data?.kind === 'personal';
  /**
   * Skills reads the same registry but the other way round — **opt-out**. Quality and Developer are
   * subsystems a workspace switches on; Skills is a menu row that already exists, so an absent row,
   * a still-loading query and a workspace nobody configured all mean "show it". Only an explicit
   * `false` hides it, which is also why this must not copy the `=== true` shape above: that would
   * make the row vanish for every workspace until an owner went and enabled it.
   * `skillsNavVisible` is the shared predicate — the manage page's toggle renders from the same one.
   */
  const showSkills =
    features.skills && skillsNavVisible(workspaceQuery.data?.domains);

  /**
   * Memory reads the registry the same opt-out way, through its own shared predicate. The row is a
   * plain link, unlike Skills: memory has a page of its own, and the map, the document and the inbox
   * do not fit in a modal.
   */
  const showMemory =
    features.memory && memoryNavVisible(workspaceQuery.data?.domains);

  /**
   * The number on the Memory row: suggestions nobody has answered yet, across both documents. It
   * feeds off the same query as the Suggestions tab, so the badge and the list can never disagree,
   * and it is refreshed by the push rather than by polling — a suggestion arrives seconds after a
   * turn ends, long after anything else on screen has settled.
   */
  const memoryProposalsQuery = useQuery({
    ...pendingMemoryProposalsQueryOptions(selectedWorkspaceId),
    enabled: showMemory && selectedWorkspaceId !== null,
  });
  const unreviewedMemoryCount = memoryProposalsQuery.data?.length ?? 0;
  /**
   * A proposal ARRIVING changes exactly one thing: the pending list this badge counts. It is not applied
   * to anything yet, so the blocks, the document and the history are all still current — and re-reading
   * them costs somebody with the Memory page open a full refetch of the section for nothing.
   */
  useServerEvent(
    'memory.proposal',
    useCallback(() => {
      if (selectedWorkspaceId === null) return;
      void qc.invalidateQueries({
        queryKey: memoryKeys.pendingProposals(selectedWorkspaceId),
      });
    }, [qc, selectedWorkspaceId]),
  );
  /**
   * A card in the chat being ANSWERED is the wider refresh, and deliberately so: accepting APPLIES the
   * proposal, which moves the blocks, the document and the history with it. Narrowing this to the
   * pending list would leave the Memory page showing the text from before the accept.
   *
   * It still stops at this workspace. That path is provider-free by design, so it cannot invalidate a
   * query itself — without this the badge would keep counting a suggestion the person just accepted, and
   * nothing in the app refetches on focus.
   */
  useEffect(
    () =>
      onMemoryProposalsChanged(() => {
        if (selectedWorkspaceId === null) return;
        void qc.invalidateQueries({
          queryKey: memoryKeys.all(selectedWorkspaceId),
        });
      }),
    [qc, selectedWorkspaceId],
  );

  const artifactsHintRef = useHintTarget<HTMLAnchorElement>(
    'nav.artifacts',
    'gate',
  );
  const memoryHintRef = useHintTarget<HTMLAnchorElement>('nav.memory', 'gate');
  const decksHintRef = useHintTarget<HTMLAnchorElement>('nav.decks', 'gate');
  const skillsHintRef = useHintTarget<HTMLButtonElement>('nav.skills', 'gate');
  /**
   * The skills directory opens as the same modal the composer's plus menu opens — one surface for
   * skills, reached from two places. The nav row is a button rather than a link for exactly that
   * reason: there is no page to navigate to.
   */
  const [skillsOpen, setSkillsOpen] = useState(false);
  const setPendingSkill = useUiStore((state) => state.setPendingSkill);
  const navigate = useNavigate();
  const routerState = useRouterState({
    select: (state) => state.location.pathname,
  });
  const codeQualityActive = useRouterState({
    select: (state) => isCodeQualityPage(state.location),
  });

  /**
   * "Use" needs a mounted composer to put the chip into. Chat and Home have one; anywhere else the
   * skill rides the `pendingSkill` carrier and we go to Home, which is where `useComposer` picks it
   * up. Staying put on a chat matters more than being consistent — navigating away from a
   * conversation to add a chip would throw the user out of what they were writing.
   */
  function handleUseSkill(skill: Skill): void {
    setPendingSkill(skill);
    setSkillsOpen(false);
    const hasComposer =
      routerState.startsWith('/chats') || routerState.startsWith('/home');
    if (!hasComposer) void navigate({ to: '/home' });
  }

  return (
    <div
      className="sidebar-root group/sidebar"
      data-state={collapsed ? 'collapsed' : 'expanded'}
    >
      {/* Header: Logo */}
      <div className="sidebar-brand px-3">
        <Link
          to="/home"
          className="sidebar-brand-link w-full min-w-0"
          onClick={onNavigate}
        >
          {/* Same fixed width as nav row icons — logo is larger but starts at this X (overflow visible). */}
          <span className="sidebar-brand-icon-rail relative shrink-0">
            <CtLogo
              variant="mark"
              tone="mono"
              className="sidebar-brand-mark aspect-square shrink-0 text-[#18399b] [[data-theme='dark']_&]:text-white"
            />
            <StatusDot identity={identity} collapsed={collapsed} />
          </span>
          <span className="sidebar-label sidebar-fade sidebar-brand-text">
            Agent
            {!collapsed && <StatusDotInline identity={identity} />}
            {!collapsed && <EnvBadge />}
          </span>
        </Link>
      </div>

      <div className={cn('px-3 pb-1 pt-1', !collapsed && 'sidebar-fade')}>
        <WorkspaceSwitcher onNavigate={onNavigate} collapsed={collapsed} />
      </div>

      <ScrollArea className="min-w-0 flex-1 overflow-hidden">
        <div className="flex flex-col gap-1 py-1">
          <div className="px-3">
            <Link
              to="/home"
              onClick={onNavigate}
              className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
            >
              <ComposeIcon className={sidebarIconClass} />
              <span className="sidebar-label sidebar-fade truncate text-left">
                New chat
              </span>
            </Link>
          </div>

          <div className="px-3">
            <Link
              ref={artifactsHintRef}
              to="/artifacts"
              onClick={onNavigate}
              className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
              activeProps={{ className: sidebarRowActive }}
            >
              <FilesIcon className={sidebarIconClass} />
              <span className="sidebar-label sidebar-fade truncate text-left">
                Artifacts
              </span>
            </Link>
          </div>

          {showTasks && (
            <div className="px-3">
              <Link
                to="/tasks"
                onClick={onNavigate}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                activeProps={{ className: sidebarRowActive }}
              >
                <ListTodoIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Tasks
                </span>
              </Link>
            </div>
          )}

          {showSkills && (
            <div className="px-3">
              <button
                ref={skillsHintRef}
                type="button"
                onClick={() => {
                  setSkillsOpen(true);
                  onNavigate?.();
                }}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
              >
                <SparklesIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Skills
                </span>
              </button>
            </div>
          )}

          {showMemory && (
            <div className="px-3">
              <Link
                ref={memoryHintRef}
                to="/memory"
                onClick={onNavigate}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                activeProps={{ className: sidebarRowActive }}
              >
                <BrainIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Memory
                </span>
                {unreviewedMemoryCount > 0 && (
                  <span
                    data-testid="memory-unreviewed-count"
                    aria-label={`${unreviewedMemoryCount} memory suggestions to review`}
                    className="sidebar-label sidebar-fade ml-auto inline-flex min-w-5 shrink-0 items-center justify-center rounded-full bg-[#1850ff] px-1.5 text-[11px] font-semibold leading-4 text-white"
                  >
                    {unreviewedMemoryCount}
                  </span>
                )}
              </Link>
            </div>
          )}

          {showDashboards && (
            <SidebarDashboards
              collapsed={collapsed}
              onNavigate={onNavigate}
              isPersonal={workspaceQuery.data?.kind === 'personal'}
            />
          )}

          {showDiagrams && (
            <div className="px-3">
              <Link
                to="/diagrams"
                onClick={onNavigate}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                activeProps={{ className: sidebarRowActive }}
              >
                <ShapesIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Diagrams
                </span>
              </Link>
            </div>
          )}

          {showAutomations && (
            <SidebarAutomations collapsed={collapsed} onNavigate={onNavigate} />
          )}

          {showDecks && (
            <div className="px-3">
              <Link
                ref={decksHintRef}
                to="/decks"
                onClick={onNavigate}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                activeProps={{ className: sidebarRowActive }}
              >
                <PresentationIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Decks
                </span>
              </Link>
            </div>
          )}

          {showQualityEntry && (
            <div className="px-3">
              <Link
                to="/quality"
                onClick={onNavigate}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                activeProps={{ className: sidebarRowActive }}
              >
                <FlaskConicalIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Quality
                </span>
              </Link>
            </div>
          )}

          {showAdminPanelLink && (
            <div className="px-3">
              <Link
                to="/admin-panel"
                onClick={() => {
                  // Auto-collapse the rail when entering the admin panel so the
                  // embedded iframe gets full horizontal real estate — its
                  // dense data tables breathe a lot easier without our 240-px
                  // sidebar eating from the side. Mobile drawer is closed by
                  // onNavigate as usual.
                  setSidebarCollapsed(true);
                  onNavigate?.();
                }}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
              >
                <ShieldCheckIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Admin panel
                </span>
              </Link>
            </div>
          )}

          {showDeveloperEntry && (
            <div className="px-3">
              <Link
                to="/developer"
                onClick={onNavigate}
                className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                activeProps={{ className: sidebarRowActive }}
              >
                <CodeIcon className={sidebarIconClass} />
                <span className="sidebar-label sidebar-fade truncate text-left">
                  Developer
                </span>
              </Link>
            </div>
          )}

          {showCodeQualityEntries && (
            <>
              <div className="px-3">
                <Link
                  to="/code-quality"
                  onClick={onNavigate}
                  aria-current={codeQualityActive ? 'page' : undefined}
                  className={cn(
                    sidebarRowBase,
                    codeQualityActive ? sidebarRowActive : sidebarRowHover,
                    'w-full',
                  )}
                >
                  <GaugeIcon className={sidebarIconClass} />
                  <span className="sidebar-label sidebar-fade truncate text-left">
                    Code Quality
                  </span>
                </Link>
              </div>
              <div className="px-3">
                <Link
                  to="/contributors"
                  onClick={onNavigate}
                  className={cn(sidebarRowBase, sidebarRowHover, 'w-full')}
                  activeProps={{ className: sidebarRowActive }}
                >
                  <UsersIcon className={sidebarIconClass} />
                  <span className="sidebar-label sidebar-fade truncate text-left">
                    Contributors
                  </span>
                </Link>
              </div>
            </>
          )}

          {!collapsed && <div className="sidebar-divider sidebar-fade" />}

          <SidebarConversations collapsed={collapsed} onNavigate={onNavigate} />
        </div>
      </ScrollArea>
      <InstallAppCard collapsed={collapsed} />

      {skillsOpen && (
        <ConnectorsDirectoryModal
          open
          onOpenChange={setSkillsOpen}
          initialCategory="skills"
          onUseSkill={handleUseSkill}
        />
      )}
    </div>
  );
}

/**
 * Whether the page is the Code Quality page of the workspace or the report of one of its projects. A
 * report opened from the Dashboards overview belongs to that overview's node instead.
 */
function isCodeQualityPage(location: {
  pathname: string;
  search?: unknown;
}): boolean {
  return (
    location.pathname === '/code-quality' ||
    (isReportPath(location.pathname) && !isOverviewReport(location))
  );
}

type BuildInfo = { env: string | null; sha: string };

function getBuildInfo(): BuildInfo {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  let env: string | null = null;
  // Order matters: a `*.dev.` / `*.stage.` host also contains the prod root
  // domain, so the dev/stage checks must run before the prod catch-all.
  if (
    !host ||
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.local')
  ) {
    env = 'Dev';
  } else if (host.includes('.dev.') || host.startsWith('dev.')) {
    env = 'Development';
  } else if (
    host.includes('.stage.') ||
    host.includes('staging') ||
    host.includes('-bridge-stage-')
  ) {
    env = 'Stage';
  } else if (
    host.includes('-bridge-prod-') ||
    host.includes('agent.ctsrnd.com')
  ) {
    env = 'Production';
  }
  return { env, sha: __APP_VERSION__ };
}

type EnvBadgeKey = 'dev' | 'stage';

const ENV_BADGE_STYLES: Record<EnvBadgeKey, string> = {
  dev: 'bg-sky-500/12 text-sky-700 border-sky-500/30 dark:text-sky-400',
  stage:
    'bg-amber-500/12 text-amber-600 border-amber-500/30 dark:text-amber-400',
};

/**
 * Maps a `getBuildInfo()` env label to the badge variant. Only dev and stage
 * get a badge — production (and unknown hosts) stay badge-free so the prod UI
 * never carries environment chrome. Colors mirror the admin console's EnvBadge.
 */
export function envBadgeVariant(
  env: string | null,
): { key: EnvBadgeKey; label: string } | null {
  if (env === 'Dev' || env === 'Development')
    return { key: 'dev', label: 'DEV' };
  if (env === 'Stage') return { key: 'stage', label: 'STAGE' };
  return null;
}

export function EnvBadge(): JSX.Element | null {
  const variant = envBadgeVariant(getBuildInfo().env);
  if (variant === null) return null;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-1.5 text-[10px] font-semibold leading-4 tracking-wide',
        ENV_BADGE_STYLES[variant.key],
      )}
      data-env={variant.key}
      aria-label={`Environment: ${variant.label}`}
    >
      {variant.label}
    </span>
  );
}

interface ServerVersion {
  sha: string;
  env?: string;
  releasedAt?: string;
}

async function fetchServerVersion(): Promise<ServerVersion | null> {
  try {
    const r = await fetch('/v1/version', { credentials: 'omit' });
    if (!r.ok) return null;
    return (await r.json()) as ServerVersion;
  } catch {
    return null;
  }
}

function formatReleasedAt(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusTooltipBody({ status }: { status: string }): JSX.Element {
  const { env, sha } = getBuildInfo();
  const q = useQuery({
    queryKey: ['server-version'],
    queryFn: fetchServerVersion,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const bundleShort = sha === 'unknown' ? null : sha.slice(0, 7);
  const serverSha =
    q.data?.sha && q.data.sha !== 'unknown' ? q.data.sha : undefined;
  const serverShort = serverSha ? serverSha.slice(0, 7) : null;
  const releasedAt = formatReleasedAt(q.data?.releasedAt);

  return (
    <div className="space-y-0.5 text-left">
      <div>
        <span className="text-muted-foreground">Status:</span>{' '}
        <span className="font-medium">{status}</span>
      </div>
      {env ? (
        <div>
          <span className="text-muted-foreground">Env:</span>{' '}
          <span className="font-medium">{env}</span>
        </div>
      ) : null}
      <div>
        <span className="text-muted-foreground">Commit:</span>{' '}
        <span className="font-mono">
          {bundleShort ?? serverShort ?? 'unknown'}
        </span>
      </div>
      {releasedAt ? (
        <div>
          <span className="text-muted-foreground">Released:</span> {releasedAt}
        </div>
      ) : null}
    </div>
  );
}

function StatusDot({
  identity,
  collapsed,
}: {
  identity: ReturnType<typeof getIdentityState>;
  collapsed: boolean;
}): JSX.Element {
  const isOnline = identity.online;
  const color = isOnline ? 'bg-emerald-500' : 'bg-destructive';
  const label = isOnline ? 'Online' : 'Offline';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full border-2 border-card',
            'transition-[opacity,transform] duration-300',
            collapsed
              ? 'opacity-100 scale-100 status-dot-pulse'
              : 'opacity-0 scale-0 pointer-events-none',
            color,
          )}
          // Pop in after the sidebar finishes collapsing (width anim = 0.06s delay + 0.3s);
          // disappear immediately on expand so it doesn't overlap the inline dot.
          style={{ transitionDelay: collapsed ? '0.36s' : '0s' }}
          aria-label={label}
          aria-hidden={!collapsed}
        />
      </TooltipTrigger>
      <TooltipContent side="right">
        <StatusTooltipBody status={label} />
      </TooltipContent>
    </Tooltip>
  );
}

function StatusDotInline({
  identity,
}: {
  identity: ReturnType<typeof getIdentityState>;
}): JSX.Element {
  const isOnline = identity.online;
  const color = isOnline ? 'bg-emerald-500' : 'bg-destructive';
  const label = isOnline ? 'Online' : 'Offline';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn('inline-block size-2 shrink-0 rounded-full', color)}
          aria-label={label}
        />
      </TooltipTrigger>
      <TooltipContent side="right">
        <StatusTooltipBody status={label} />
      </TooltipContent>
    </Tooltip>
  );
}
